import styled, { css } from 'styled-components';
import { staticURL } from 'core/url';
import { media, customMedia } from 'styles/utils';

import { Heading, Paragraph, Button, Icon } from 'components';

import { Commenters } from 'apps/components/commenters';

const squaresStyles = (size, squaresInvert) => css`
  position: relative;
  &::before,
  &::after {
    content: '';
    width: ${size === 'small' ? '75px' : '70%'};
    max-width: 400px;
    height: ${size === 'small' ? '75px' : '50%'};
    display: block;
    position: absolute;
    border-radius: ${props => props.theme.borderRadius.medium};
    box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.18);
    z-index: 0;
    transition: opacity 0.2s ease, all 0.2s ease;
    opacity: 1;
  }
  &::before {
    top: ${size === 'small' ? '-8px' : '-1.5vw'};
    right: ${size === 'small' ? '-8px' : '-1.5vw'};
    background-color: ${squaresInvert ? '#8d98ff' : '#ff9500'};
  }
  &::after {
    bottom: ${size === 'small' ? '-8px' : '-1.5vw'};
    left: ${size === 'small' ? '-8px' : '-1.5vw'};
    background-color: ${squaresInvert ? '#ff9500' : '#8d98ff'};
  }
`;

const imageFigure = css`
  img {
    width: 100%;
    border-radius: ${props => props.theme.borderRadius.medium};
    box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.18);
    filter: grayscale(100%);
    transition: filter ease 1s;
    will-change: filter;
  }
  /* ps:selectors */
  &:hover {
    cursor: pointer;
    img {
      filter: grayscale(0);
    }
  }
`;

export const ViralResponseLandingStyled = styled.section`
  font-family: Montserrat;
`;

export const HeadingStyled = styled(Heading)`
  color: ${props => props.theme.color.wfBlack};
  margin: 0 0 ${props => props.theme.spacing.xlarge} 0;
  /* Media queries */
  ${media.large`
    font-size: ${props => props.theme.font.size.xmedium};
  `}
`;

export const HeadingExtra = styled(Heading)`
  font-size: ${props => props.theme.font.size.large};
  font-weight: 800;
  margin: 0 0 ${props => props.theme.spacing.medium} 0;
  color: ${props => props.theme.color.wfBlack};
  /* Media queries */
  ${media.large`
    font-size: ${props => props.theme.font.size.xlarge};
  `}
`;

export const HeadingExtraInline = styled(HeadingExtra)`
  display: inline-block;
  margin-bottom: 0;
  vertical-align: middle;
`;

export const ButtonStyled = styled(Button)`
  width: 100%;
  margin-top: ${props => props.theme.spacing.medium};
  /* Media queries */
  ${media.medium`
    width: initial;
  `}
  ${media.large`
    font-size: ${props => props.theme.font.size.medium};
  `}
`;

export const Hero = styled.article`
  position: relative;
  /* Media queries */
  ${media.large`
    display: grid;
    grid-template-columns: 1.3fr 2fr;
    /* ps:elements */
    &::after {
      content: '';
      display: block;
      transform: rotateY(180deg);
      background: url(${staticURL(
        '/assets/whitelabels/viralresponse/oval-orange.svg',
      )})
        bottom no-repeat;
      position: absolute;
      background-size: contain;
      bottom: 0;
      width: 100px;
      height: 40vh;
      z-index: -1;
    }
  `}
`;

export const Info = styled.div`
  padding: ${props => props.theme.spacing.default} 5vw;
  position: relative;
  /* Media queries */
  ${media.large`
    padding: 8vw 0 0 5vw;
    p {
      width: 85%;
      margin-bottom: ${props => props.theme.spacing.medium};
    }
  `}
`;

export const SmallRotated = styled.small`
  color: grey;
  transform: rotate(270deg);
  font-size: ${props => props.theme.font.size.default};
  position: absolute;
  left: 5%;
  top: 38%;
  transform-origin: top left;
  /* Media queries */
  ${customMedia.lessThan('large')`
    display: none;
  `}
`;

export const Collage = styled.div`
  position: relative;
  background: url(${staticURL(
      '/assets/whitelabels/viralresponse/combined-shape.svg',
    )})
    no-repeat 40% 0 / cover;
  padding: 10vw 5vw 20vw;
  text-align: center;
  p {
    text-align: left;
    display: block;
    margin: 0 auto 20px;
    line-height: 30px;
    max-width: 650px;
    strong {
      font-size: 120%;
    }
  }
  /* Media queries */
  ${media.large`
    padding: 60px 0 20% 5vw;
    background-size: 120%;
    background-position: 0 100%;
  `}
`;

export const FigureCollage = styled.figure`
  display: inline-block;
  margin: 0 auto 40px;
  ${squaresStyles}
  /* Media queries */
  ${media.large`
    width: 70%;
  `}
`;

export const ImgCollage = styled.img`
  width: 100%;
  max-width: 100%;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.33);
  display: block;
  position: relative;
  z-index: 5;
`;

export const TextCollage = styled.div`
  position: relative;
  /* ps:elements */
  &::before {
    width: 15px;
    height: 6vw;
    display: block;
    background: url(${staticURL(
        '/assets/whitelabels/viralresponse/orange-line.svg',
      )})
      no-repeat;
    background-position: bottom;
    background-size: cover;
    transform: rotate(270deg);
    transform-origin: top;
    position: absolute;
    top: 16px;
    left: -5vw;
    /* Media queries */
    ${media.xlarge`
      content: '';
    `}
  }
`;

export const NavSocial = styled.ul`
  text-align: right;
  position: absolute;
  right: 0;
  bottom: -${props => props.theme.spacing.large};
  /* Media queries */
  ${customMedia.lessThan('large')`
    li {
      display: inline-block;
    }
  `}
  ${media.large`
    right: -13%;
    top: 50%;
    bottom: initial;
    transform: translateY(-50%);
  `}
`;

export const IconStyled = styled(Icon)`
  color: ${props => props.theme.color.wfBlack};
  margin-left: ${props => props.theme.spacing.default};
  transition: color ease 0.2s;
  &:hover {
    color: ${props => props.theme.color.primary};
  }
  /* Media queries */
  ${media.large`
    display: block;
    margin-bottom: ${props => props.theme.spacing.medium};
    margin-left: 0;
  `}
`;

export const ArrowImage = styled.img`
  position: absolute;
  height: 20vw;
  margin-top: -${props => props.theme.spacing.small};
  /* Media queries */
  ${media.large`
    height: 10vw;
  `}
`;

export const LandingSection = styled.section`
  text-align: ${props => props.textAlign || 'left'};
  padding: 5vw;
  background: ${props =>
    props.issues
      ? `url(${staticURL('/assets/whitelabels/viralresponse/oval-purple.svg')})
    no-repeat left / contain;`
      : props.projects &&
        `url(${staticURL('/assets/whitelabels/viralresponse/oval-orange.svg')})
    no-repeat bottom right;`};
  background: ${props =>
    props.slack &&
    `url(${staticURL('/assets/whitelabels/viralresponse/oval-purple.svg')})
    no-repeat left 0% / 20%, url(${staticURL(
      '/assets/whitelabels/viralresponse/oval-orange.svg',
    )})
    no-repeat top right;`};
`;

export const ButtonSectionStyled = styled(Button)`
  width: 100%;
  margin: 0 auto;
  display: block;
  /* Media queries */
  ${media.medium`
    width: initial;
  `}
  ${media.large`
    font-size: ${props => props.theme.font.size.medium};
  `}
`;

export const Projects = styled.div`
  display: grid;
  grid-gap: 10vw;
  padding-bottom: ${props => props.theme.spacing.xxlarge};
  /* Media queries */
  ${media.medium`
    grid-gap: 5vw;
    grid-template-columns: repeat(2, minmax(200px, 1fr));
  `}
  ${media.large`
    grid-template-columns: repeat(3, minmax(250px, 1fr));
    grid-gap: 2vw;
  `}
`;

export const Project = styled.figure`
  /* TODO: To add global resetter margin and images */
  margin: ${props => props.theme.font.size.medium} 0 0 0;
  position: relative;
  /* Media queries */
  ${customMedia.lessThan('medium')`
    &:nth-child(n+4) {
      display: none;
    }
  `}
  ${imageFigure}
  figcaption {
    width: 90%;
    background-color: #8d98ff;
    padding: ${props => props.theme.spacing.medium};
    border-radius: ${props => props.theme.borderRadius.medium};
    text-align: center;
    font-weight: 800;
    color: ${props => props.theme.color.wfWhite};
    box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.22);
    position: absolute;
    bottom: -${props => props.theme.spacing.medium};
    left: 50%;
    transform: translateX(-50%);
    /* Media queries */
    ${media.medium`
      grid-template-columns: repeat(2, minmax(200px, 1fr));
    `}
    ${media.large`
      font-size: ${props => props.theme.font.size.medium};
    `}
  }
`;

export const LandingSectionContent = styled.div`
  padding: ${props => props.theme.font.size.medium} 0;
  min-height: 20vh;
  position: relative;
`;

export const Issue = styled.article`
  margin-bottom: ${props => props.theme.spacing.large};
  > div {
    text-align: left;
    color: ${props => props.theme.color.wfGrey};
    box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.16);
    display: flex;
    border-radius: ${props => props.theme.borderRadius.medium};
    background-color: #fff;
    align-items: flex-start;
    justify-content: flex-start;
    z-index: 5;
    position: relative;
    transition: transform 0.2s ease 0.1s;
    will-change: transform;
    padding: ${props =>
      `${props.theme.spacing.small} ${props.theme.spacing.medium}`};
    /* Media queries */
    ${media.large`
      align-items: center;
      header {
        max-width: 55%;
        margin-right: auto;
      }
    `}
  }
  h3 {
    margin: 0 0 ${props => props.theme.spacing.small} 0;
  }
  small {
    font-size: 100%;
    b {
      color: ${props => props.theme.color.wfGrey500};
      font-weight: 300;
    }
  }
  h3:hover,
  small b:hover {
    /* ps:selectors */
    &:hover {
      color: ${props => props.theme.color.primary};
      text-decoration: underline;
    }
  }
  &:not(:first-child) {
    margin-top: ${props => props.theme.spacing.default};
  }
  button {
    /* Media queries */
    ${customMedia.lessThan('large')`
      display: none;
    `}
  }
  ${props => squaresStyles('small', props.squaresInvert)}
`;

export const CommentersStyled = styled(Commenters)`
  margin-left: auto;
  margin-right: ${props => props.theme.spacing.xsmall};
`;

export const Avatar = styled.img`
  width: 9vw;
  height: 9vw;
  max-width: 64px;
  max-height: 64px;
  border-radius: 50%;
  vertical-align: bottom;
  margin-right: ${props =>
    props.small ? props.theme.spacing.small : props.theme.spacing.medium};
`;

export const ParagraphStory = styled(Paragraph)`
  color: ${props => props.theme.color.primary};
  font-size: ${props => props.theme.font.size.default};
  font-weight: 800;
  text-decoration: underline;
  /* Media queries */
  ${media.large`
    font-size: ${props => props.theme.font.size.medium};
   `}
`;

export const Story = styled.figure`
  ${props => squaresStyles('false', props.squaresInvert)}
  margin: 0 0 20vw 0;
  ${imageFigure}
  img {
    max-height: 400px;
    position: relative;
    z-index: 5;
    border-radius: ${props => props.theme.borderRadius.medium};
    object-fit: cover;
    /* Media queries */
    ${customMedia.lessThan('large')`
      min-height: 50vw;
      object-fit: cover;
      object-position: left;
    `}
  }
  figcaption {
    width: 100%;
    position: absolute;
    z-index: 5;
    text-align: right;
    top: 110%;
    /* Media queries */
    ${media.large`
      width: 50%;
      top: 102%;
      right: 0;
    `}
  }
  /* ps:selectors */
  &:hover {
    ${ParagraphStory} {
      text-decoration: none;
    }
  }
  /* Media queries */
  ${media.large`
    margin: 0 0 10vw 0;
  `}
`;

export const SlackLogo = styled.img`
  display: inline-block;
  vertical-align: middle;
  width: 8vw;
  height: 8vw;
  margin-right: ${props => props.theme.spacing.medium};
  max-width: 100px;
`;
